import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { planApi } from '../api/planApi';
import type { Plan } from '../types/plan';
import { useUser } from '@shared/hooks/useUser';
import { useImpersonationGuard } from '@shared/components/ImpersonationGuard';
import { useDialog } from '@shared/hooks/useDialog';
import { getToken } from '@shared/services/api';
import { invalidateShared, SHARED_KEYS } from '@shared/services/sharedResource';
import { Card, Button, Input } from '@shared/components/ui';

interface Props {
  organizationId?: string;
  onGranted: () => void | Promise<void>;
}

export default function AdminPlanGrant({ organizationId, onGranted }: Props) {
  const { t } = useTranslation(['admin', 'billing']);
  const { user } = useUser();
  const { shouldDisable } = useImpersonationGuard();
  const { showConfirm } = useDialog();
  const id = useId();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [planId, setPlanId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const isAdmin = user?.is_platform_admin === true;

  useEffect(() => {
    if (!isAdmin || shouldDisable) return;
    planApi
      .getPlans(true)
      .then(setPlans)
      .catch(() => setMessage(t('adminPlanGrant.errors.loadPlans')));
  }, [isAdmin, shouldDisable]);

  if (!isAdmin || shouldDisable) return null;

  const grant = async () => {
    const plan = plans.find((item) => item.id === planId);
    if (!plan || busy) return;
    const confirmed = await showConfirm(
      t('adminPlanGrant.confirmTitle'),
      t('adminPlanGrant.confirmMessage', {
        plan: plan.displayName,
        organization: t(
          organizationId ? 'adminPlanGrant.thisOrganization' : 'adminPlanGrant.yourOrganization'
        ),
      }),
      'confirm'
    );
    if (!confirmed) return;
    setBusy(true);
    setMessage('');
    try {
      const response = await fetch('/api/v1/admin/subscription/grant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          plan_id: planId,
          reason: reason.trim(),
          ...(organizationId ? { organization_id: organizationId } : {}),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('adminPlanGrant.errors.grantPlan'));
      if (!organizationId) {
        invalidateShared(SHARED_KEYS.subscription);
        window.dispatchEvent(new Event('pgkb:subscription-changed'));
      }
      setMessage(data.message);
      await onGranted();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('adminPlanGrant.errors.grantPlan'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 space-y-4">
      <h2 className="text-xl font-bold">{t('adminPlanGrant.title')}</h2>
      <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
        {organizationId
          ? t('adminPlanGrant.forOrganization')
          : t('adminPlanGrant.forOwnOrganization')}{' '}
        {t('adminPlanGrant.noChargeAudit')}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${id}-plan`} className="block text-sm font-medium mb-2">
            {t('adminPlanGrant.plan')}
          </label>
          <select
            id={`${id}-plan`}
            value={planId}
            onChange={(event) => setPlanId(event.target.value)}
            disabled={busy}
            className="w-full rounded-lg border border-border-light dark:border-border-dark bg-white dark:bg-slate-900 p-3"
          >
            <option value="">{t('adminPlanGrant.selectPlan')}</option>
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {t(`billing:pricing.planNames.${plan.name}`, {
                  defaultValue: plan.displayName || plan.name,
                })}{' '}
                — {t('adminPlanGrant.agentCount', { count: plan.agentsIncluded })} /{' '}
                {plan.storageIncludedGB} GB
              </option>
            ))}
          </select>
        </div>
        <Input
          id={`${id}-reason`}
          label={t('adminPlanGrant.reason')}
          value={reason}
          maxLength={500}
          disabled={busy}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t('adminPlanGrant.reasonPlaceholder')}
        />
      </div>
      {message && (
        <p role="status" className="text-sm">
          {message}
        </p>
      )}
      <Button
        onClick={grant}
        disabled={!planId || reason.trim().length < 3 || busy}
        isLoading={busy}
      >
        {t('adminPlanGrant.grantButton')}
      </Button>
    </Card>
  );
}
